import { BRAND } from "../constants/brand";
import PageHero from "../components/PageHero";

const VALUES = [
  { title: "Excellence",  icon: "★", desc: "We prioritize hiring quality staff who deliver quality results, recognizing that a great workplace environment drives superior performance across every test we process." },
  { title: "Quality",     icon: "◆", desc: "Advanced instrumentation, certified personnel, and sophisticated reporting systems position Labarium as a leading national laboratory with CAP and CLIA certifications." },
  { title: "Integrity",   icon: "⬡", desc: "Our comprehensive compliance program ensures regulatory adherence at every level. We maintain transparency with patients, providers, and payers in all that we do." },
  { title: "Leadership",  icon: "▲", desc: "Management creates safe, quality work environments that support both company growth and team development. Every team member is empowered to lead within their role." },
  { title: "Teamwork",    icon: "◉", desc: "Our staff collaborates respectfully across departments with emphasis on loyalty and professional dignity — because the best results come from working together." },
  { title: "Innovation",  icon: "⬢", desc: "From AI-powered diagnostic insights to EMR integrations, we continuously invest in technology that makes clinical decision-making faster, smarter, and more accurate." },
];

const CERTS = ["CAP Accredited", "CLIA Certified", "OIG Compliance Program", "Medicare Approved"];

const GLANCE = [
  ["1,500+",   "Specimens processed daily"],
  ["400+",     "Tests in our diagnostic menu"],
  ["20+",      "States served nationwide"],
  ["35+",      "Insurance plans accepted"],
  ["200,000+", "Patients tested annually"],
  ["24 hrs",   "Typical result turnaround"],
];

export default function AboutPage({ setPage }) {
  return (
    <>
      <PageHero
        eyebrow="Company"
        title="About"
        highlight="Labarium"
        subtitle="The Healthcare Decision Company — transforming laboratory results into actionable insights that improve patient outcomes across the United States."
      />

      <div style={{ background: BRAND.offWhite }}>

        {/* ── MISSION ── */}
        <div className="rx-pad" style={{ maxWidth: 1400, margin: "0 auto", padding: "96px 40px 80px" }}>
          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center" }}>

            {/* Text */}
            <div className="reveal-left">
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.25em", color: BRAND.gold, marginBottom: 14, textTransform: "uppercase" }}>Our Mission</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(28px,4vw,44px)", color: BRAND.navy, margin: "0 0 22px", lineHeight: 1.15 }}>
                Deliver the Highest Standard of Care in Laboratory Testing
              </h2>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: BRAND.textMid, lineHeight: 1.9, marginBottom: 18, fontWeight: 400 }}>
                Labarium Diagnostic Systems was founded with a singular vision: to build The Healthcare Decision Company — a next-generation clinical laboratory that goes beyond reporting numbers to providing the context that drives better clinical decisions.
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: BRAND.textMid, lineHeight: 1.9, marginBottom: 32, fontWeight: 400 }}>
                Through the acquisition of laboratories and sales teams, Labarium is building a unified national platform that combines proven laboratory excellence with cutting-edge AI-powered diagnostic technology.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {CERTS.map(c => (
                  <span key={c} style={{
                    background: BRAND.navy,
                    color: BRAND.gold,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 11, fontWeight: 600, letterSpacing: "0.1em",
                    padding: "6px 14px", borderRadius: 7,
                    border: "1px solid rgba(201,169,110,0.2)",
                  }}>{c}</span>
                ))}
              </div>
            </div>

            {/* At a glance card */}
            <div className="reveal-right" style={{
              background: `linear-gradient(150deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`,
              borderRadius: 24, padding: "48px 44px",
              position: "relative", overflow: "hidden",
              boxShadow: "0 24px 72px rgba(0,14,36,0.35)",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,169,110,0.08) 0%, transparent 65%)" }} />
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", color: BRAND.gold, marginBottom: 24, textTransform: "uppercase" }}>At a Glance</div>
              {GLANCE.map(([val, label], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: i < GLANCE.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.55)", fontWeight: 400 }}>{label}</span>
                  <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 26, color: BRAND.gold }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CORE VALUES ── */}
        <div className="rx-pad" style={{ borderTop: `1px solid ${BRAND.lightGray}`, padding: "96px 40px" }}>
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                <div style={{ width: 48, height: 1, background: BRAND.gold }} />
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Core Values</span>
                <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              </div>
              <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(28px,4vw,44px)", color: BRAND.navy, margin: 0 }}>
                What We Stand For
              </h2>
            </div>

            <div className="three-col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
              {VALUES.map((v, i) => (
                <div
                  key={i}
                  className={`reveal delay-${(i % 3 + 1) * 100}`}
                  style={{
                    background: BRAND.white,
                    borderRadius: 18,
                    padding: "36px 32px",
                    border: `1px solid ${BRAND.lightGray}`,
                    boxShadow: "0 2px 16px rgba(0,30,80,0.05)",
                    position: "relative", overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 20px 56px rgba(0,30,80,0.13)";
                    e.currentTarget.style.borderColor = "rgba(201,169,110,0.3)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,30,80,0.05)";
                    e.currentTarget.style.borderColor = BRAND.lightGray;
                  }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${BRAND.navy}, ${BRAND.navyLight})` }} />
                  <div style={{ position: "absolute", top: 16, right: 20, fontFamily: "'Playfair Display', serif", fontSize: 64, fontWeight: 700, color: "rgba(0,53,122,0.04)", lineHeight: 1 }}>0{i + 1}</div>

                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navyLight})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: BRAND.gold, marginBottom: 20, fontSize: 18,
                    boxShadow: "0 6px 20px rgba(0,53,122,0.25)",
                  }}>
                    {v.icon}
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: BRAND.navy, margin: "0 0 12px" }}>{v.title}</h3>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: BRAND.textMid, lineHeight: 1.85, margin: 0, fontWeight: 400 }}>{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── LOCATIONS ── */}
        <div className="rx-pad" style={{ padding: "0 40px 96px" }}>
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>

            {/* Section heading */}
            <div className="reveal" style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                <div style={{ width: 48, height: 1, background: BRAND.gold }} />
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Our Locations</span>
                <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(24px,3vw,36px)", color: BRAND.navy, margin: 0 }}>
                Southern-Based, National Platform
              </h2>
            </div>

            {/* Corporate Headquarters */}
            <div className="reveal" style={{
              background: BRAND.white, borderRadius: 20,
              border: `1px solid ${BRAND.lightGray}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,30,80,0.08)",
              marginBottom: 32,
            }}>
              <div style={{
                background: `linear-gradient(150deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`,
                padding: "28px 36px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 16, position: "relative",
              }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />
                <div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.22em", color: BRAND.gold, marginBottom: 5, textTransform: "uppercase" }}>Corporate Headquarters</div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.white, margin: 0 }}>Miramar Beach, Florida</h2>
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, textAlign: "right", fontWeight: 400 }}>
                  <div>495 Grand Blvd #206 · Miramar Beach, FL 32550</div>
                  <div>(850) 460-1484 · Toll-Free: (877) 765-2274</div>
                  <div>info@labarium.com</div>
                </div>
              </div>
              <iframe
                title="Labarium Corporate Headquarters"
                width="100%" height="340" frameBorder="0"
                style={{ border: 0, display: "block" }}
                allowFullScreen loading="lazy"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3451.2!2d-86.3683!3d30.3797!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x889164f3a5f7b75b%3A0x1!2s495%20Grand%20Blvd%20%23206%2C%20Miramar%20Beach%2C%20FL%2032550!5e0!3m2!1sen!2sus!4v1700000000001!5m2!1sen!2sus"
              />
            </div>

            {/* Florida Laboratory */}
            <div className="reveal delay-100" style={{
              background: BRAND.white, borderRadius: 20,
              border: `1px solid ${BRAND.lightGray}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,30,80,0.08)",
              marginBottom: 32,
            }}>
              <div style={{
                background: `linear-gradient(150deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`,
                padding: "28px 36px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 16, position: "relative",
              }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />
                <div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.22em", color: BRAND.gold, marginBottom: 5, textTransform: "uppercase" }}>Florida Laboratory, Infiniti Labs</div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.white, margin: 0 }}>Tampa, Florida</h2>
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, textAlign: "right", fontWeight: 400 }}>
                  <div>6015 Benjamin Rd #315-A · Tampa, FL 33634</div>
                  <div>(850) 460-1484 · Toll-Free: (877) 765-2274</div>
                  <div>info@labarium.com</div>
                </div>
              </div>
              <iframe
                title="Labarium Florida Laboratory"
                width="100%" height="340" frameBorder="0"
                style={{ border: 0, display: "block" }}
                allowFullScreen loading="lazy"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3524.4!2d-82.5614!3d27.9824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c2c49a5e9bcd7f%3A0x1!2s6015%20Benjamin%20Rd%20%23315-A%2C%20Tampa%2C%20FL%2033634!5e0!3m2!1sen!2sus!4v1700000000002!5m2!1sen!2sus"
              />
            </div>

            {/* Labarium Affiliated Laboratory, Med-Lake Laboratory */}
            <div className="reveal delay-200" style={{
              background: BRAND.white, borderRadius: 20,
              border: `1px solid ${BRAND.lightGray}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,30,80,0.08)",
            }}>
              <div style={{
                background: `linear-gradient(150deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`,
                padding: "28px 36px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 16, position: "relative",
              }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />
                <div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.22em", color: BRAND.gold, marginBottom: 5, textTransform: "uppercase" }}>Labarium Affiliated Laboratory</div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.white, margin: 0 }}>Med-Lake Laboratory, Georgia</h2>
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, textAlign: "right", fontWeight: 400 }}>
                  <div>100 Industrial Park Drive · Milledgeville, GA 31061</div>
                  <div>(478) 414-4235 · Fax: (478) 414-4247</div>
                </div>
              </div>
              <iframe
                title="Labarium Affiliated Laboratory, Med-Lake Laboratory"
                width="100%" height="340" frameBorder="0"
                style={{ border: 0, display: "block" }}
                allowFullScreen loading="lazy"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3328.7!2d-83.2324!3d33.0801!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f3f56c9e1f6b77%3A0x5e3ca5f58bdf8a5!2s100%20Industrial%20Park%20Dr%2C%20Milledgeville%2C%20GA%2031061!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              />
            </div>

            {/* CTA */}
            <div className="reveal delay-200" style={{
              marginTop: 36,
              background: `linear-gradient(135deg, ${BRAND.navyDeep}, ${BRAND.navy})`,
              borderRadius: 18,
              padding: "48px 52px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 28,
              position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, transparent)` }} />
              <div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.22em", color: BRAND.gold, marginBottom: 10, textTransform: "uppercase" }}>Meet the Team</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 30, color: BRAND.white, margin: 0 }}>
                  Learn about the leaders behind Labarium
                </h3>
              </div>
              <button
                onClick={() => setPage("leadership-team")}
                style={{
                  background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                  backgroundSize: "200% auto",
                  color: BRAND.navyDeep,
                  border: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 700, fontSize: 12,
                  letterSpacing: "0.08em",
                  padding: "16px 36px", borderRadius: 10, cursor: "pointer",
                  flexShrink: 0, textTransform: "uppercase",
                  boxShadow: "0 8px 28px rgba(201,169,110,0.35)",
                }}
                className="btn-shimmer"
              >
                Leadership Team →
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
